import React, { useState } from "react";
import { Clock, CheckSquare, Square, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export function dueState(t) {
  if (!t?.due_at) return null;
  if (t.due_done) return "done";
  const diff = new Date(t.due_at).getTime() - Date.now();
  if (diff < 0) return "overdue";
  return diff < 86400000 ? "soon" : "future";
}

const STYLE = {
  future: "bg-norea-surfaceHover text-norea-text border-norea-border",
  soon: "bg-amber-100 text-amber-900 border-amber-400",
  overdue: "bg-red-600 text-white border-red-700",
  done: "bg-emerald-600 text-white border-emerald-700",
};
const LABEL = { future: "À venir", soon: "Moins de 24 h", overdue: "En retard", done: "Terminé" };

export const fmtDue = (t) =>
  new Date(t.due_at).toLocaleString("fr-FR", t.due_has_time ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" } : { day: "2-digit", month: "short" });

export async function saveDue(basePath, id, body) {
  try {
    await api.patch(`${basePath}/${id}/due`, body);
    return true;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Échéance non enregistrée");
    return false;
  }
}

export function DueBadge({ ticket, basePath, onChange }) {
  const st = dueState(ticket);
  if (!st) return null;
  const toggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (await saveDue(basePath, ticket.id, { due_at: ticket.due_at, due_has_time: !!ticket.due_has_time, due_done: !ticket.due_done })) onChange?.();
  };
  const Box = ticket.due_done ? CheckSquare : Square;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${STYLE[st]}`} title={LABEL[st]} data-testid={`ticket-${ticket.id}-due`} data-state={st}>
      <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={toggle} className="opacity-80 hover:opacity-100" title={ticket.due_done ? "Marquer non terminé" : "Marquer terminé"} data-testid={`ticket-${ticket.id}-due-toggle`}>
        <Box className="w-3 h-3" />
      </button>
      <Clock className="w-3 h-3" /> {fmtDue(ticket)}
    </span>
  );
}

const pad = (n) => String(n).padStart(2, "0");
const toLocalParts = (iso) => {
  if (!iso) return ["", ""];
  const d = new Date(iso);
  return [`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, `${pad(d.getHours())}:${pad(d.getMinutes())}`];
};
export const buildDueIso = (date, time) => (date ? new Date(`${date}T${time || "23:59"}:00`).toISOString() : null);

export function DueFields({ date, time, onDate, onTime, testid = "ticket-due" }) {
  return (
    <div className="flex items-center gap-2">
      <input type="date" value={date} onChange={(e) => onDate(e.target.value)} className="input-field text-sm max-w-[170px]" data-testid={`${testid}-date`} />
      <input type="time" value={time} onChange={(e) => onTime(e.target.value)} disabled={!date} className="input-field text-sm max-w-[120px]" placeholder="Heure" data-testid={`${testid}-time`} />
    </div>
  );
}

export function DueEditor({ ticket, basePath }) {
  const [d0, t0] = toLocalParts(ticket.due_at);
  const [date, setDate] = useState(d0);
  const [time, setTime] = useState(ticket.due_has_time ? t0 : "");
  const [done, setDone] = useState(!!ticket.due_done);
  const persist = (nd, nt, ndone) => saveDue(basePath, ticket.id, { due_at: buildDueIso(nd, nt), due_has_time: !!(nd && nt), due_done: !!(nd && ndone) });
  const st = dueState({ due_at: buildDueIso(date, time), due_done: done });
  return (
    <div data-testid="ticket-due-editor">
      <label className="overline block mb-1.5">Échéance</label>
      <div className="flex items-center gap-3 flex-wrap">
        <DueFields date={date} time={time} onDate={(v) => { setDate(v); persist(v, time, done); }} onTime={(v) => { setTime(v); persist(date, v, done); }} />
        {date && (
          <>
            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={done} onChange={(e) => { setDone(e.target.checked); persist(date, time, e.target.checked); }} className="accent-norea-primary w-4 h-4" data-testid="ticket-due-done" /> Terminé
            </label>
            {st && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${STYLE[st]}`} data-testid="ticket-due-state">{LABEL[st]}</span>}
            <button type="button" onClick={() => { setDate(""); setTime(""); setDone(false); persist("", "", false); }} className="text-xs text-norea-textMuted hover:text-red-600 inline-flex items-center gap-1" data-testid="ticket-due-clear">
              <X className="w-3 h-3" /> Retirer
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function matchesDueFilter(t, f) {
  if (!f) return true;
  const st = dueState(t);
  if (f === "none") return !st;
  if (f === "overdue") return st === "overdue";
  if (!st || st === "done") return false;
  const due = new Date(t.due_at);
  const now = new Date();
  if (f === "today") return due.toDateString() === now.toDateString();
  if (f === "week") return due.getTime() - now.getTime() < 7 * 86400000;
  return true;
}
